import React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { SUPPORTED_LOCALES } from '../locales/locale';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { LocaleFlag } from './LocaleFlag';

export const languageSelectOnDarkContentClassName =
  'bg-[#2f2f31] text-white border-white/12';

export const languageSelectOnDarkItemClassName =
  'focus:bg-white/10 focus:text-white';

interface LanguageSelectProps {
  className?: string;
  id?: string;
  variant?: 'default' | 'onDark';
  menuSide?: 'top' | 'bottom';
}

export const LanguageSelect: React.FC<LanguageSelectProps> = ({
  className,
  id,
  variant = 'default',
  menuSide = 'bottom',
}) => {
  const { locale, setLocale, t } = useLocale();
  const currentName = t(`locale_name_${locale}`);
  const onDark = variant === 'onDark';

  return (
    <TooltipProvider delayDuration={200}>
      <DropdownMenu>
        <ButtonTooltip tooltip={currentName} side={menuSide}>
          <DropdownMenuTrigger asChild>
            <button
              id={id}
              type="button"
              aria-label={t('language')}
              className={cn(
                'inline-flex h-8 items-center gap-1 rounded-md px-1.5',
                'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[rgba(94,204,128,0.4)]',
                onDark
                  ? 'text-white hover:bg-white/10'
                  : 'text-foreground hover:bg-foreground/10',
                className,
              )}
            >
              <LocaleFlag locale={locale} className="h-3.5 w-[1.3125rem]" />
              <ChevronDown className="h-3 w-3 opacity-50" />
            </button>
          </DropdownMenuTrigger>
        </ButtonTooltip>
        <DropdownMenuContent
          align="end"
          side={menuSide}
          className={cn('z-[2010] min-w-[10rem]', onDark && languageSelectOnDarkContentClassName)}
        >
          {SUPPORTED_LOCALES.map((code) => {
            const name = t(`locale_name_${code}`);
            const selected = locale === code;

            return (
              <DropdownMenuItem
                key={code}
                onClick={() => setLocale(code)}
                className={onDark ? languageSelectOnDarkItemClassName : undefined}
              >
                <LocaleFlag locale={code} className="h-3.5 w-[1.3125rem]" />
                <span className="flex-1">{name}</span>
                {selected ? <Check className="ml-auto h-3.5 w-3.5 opacity-70" /> : null}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};
